import { useCallback, useEffect, useRef, useState } from 'react';

// The tooltip's widest size (CSS: max-width min(320px, 80vw)) and the gap
// it keeps from the edges of the visible page.
export const TIP_MAX_WIDTH = 320;
export const TIP_GUTTER = 8;

// The visible width: clientWidth leaves out a classic scrollbar.
const viewportWidth = () => document.documentElement.clientWidth || window.innerWidth || 0;

// Viewport position just below the trigger, centred on it, with the centre
// moved in far enough that a tooltip of the maximum width stays on screen
// (the tooltip is centred on `left` with translateX(-50%)).
export const tipPosition = (el) => {
    const rect = el?.getBoundingClientRect();
    if (!rect) return { top: 0, left: 0 };
    const centre = rect.left + rect.width / 2;
    const vw = viewportWidth();
    if (!vw) return { top: rect.bottom + 8, left: centre };
    const half = Math.min(TIP_MAX_WIDTH, vw * 0.8) / 2;
    const left = Math.min(Math.max(centre, TIP_GUTTER + half), vw - TIP_GUTTER - half);
    return { top: rect.bottom + 8, left };
};

// The one tooltip open on the page: opening another closes it (R2-B3).
let closeActive = null;

/**
 * State for a tooltip that is opened by state and positioned `fixed`, so a
 * scrolling container can't clip it; while open it follows the trigger on
 * scroll (page or container, captured) and resize. Extracted from the AI price
 * header tip so the v2 Tooltip primitive behaves exactly the same.
 *
 * Only one tooltip is open at a time, and an open one closes as soon as the
 * pointer is over anything outside its trigger, even when the browser sent no
 * leave event (the trigger scrolled away under a still pointer). A scroll
 * never reopens a tooltip that was just closed (QA Re-test 2, R2-B3: Deal
 * status chip tooltips stayed open, several at once).
 */
export default function useAnchoredTooltip() {
    const triggerRef = useRef(null);
    const [pos, setPos] = useState(null);
    const isOpen = pos !== null;

    const close = useCallback(() => setPos(null), []);

    useEffect(() => {
        if (!isOpen) return undefined;
        // Only moves an open tooltip: a scroll queued right after a close
        // must not bring it back.
        const follow = () => setPos((p) => (p ? tipPosition(triggerRef.current) : p));
        const pointerElsewhere = (e) => {
            const el = triggerRef.current;
            if (el && e.target instanceof Node && !el.contains(e.target)) close();
        };
        window.addEventListener('scroll', follow, true);
        window.addEventListener('resize', follow);
        document.addEventListener('mouseover', pointerElsewhere);
        return () => {
            window.removeEventListener('scroll', follow, true);
            window.removeEventListener('resize', follow);
            document.removeEventListener('mouseover', pointerElsewhere);
            if (closeActive === close) closeActive = null;
        };
    }, [isOpen, close]);

    // Unmounting while open: forget it as the open one.
    useEffect(() => () => { if (closeActive === close) closeActive = null; }, [close]);

    const open = useCallback(() => {
        if (closeActive && closeActive !== close) closeActive();
        closeActive = close;
        setPos(tipPosition(triggerRef.current));
    }, [close]);

    return { triggerRef, pos, isOpen, open, close };
}
